import { DateTime, Settings } from 'luxon';
import { JobScheduleDto, JobFrequency as Freq, DayOfWeek } from '@/api/generator';

const defaultWeekdays: DayOfWeek[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

interface Schedule {
  frequency?: Freq | null;
  periodStart?: DateTime;
  periodEnd?: DateTime;
  executionStart?: DateTime;
  executionEnd?: DateTime;
  executionAllDay?: boolean;
  weekdays?: DayOfWeek[] | null;
  interval?: number;
  timeZone?: string | null;
}

function now() {
  return DateTime.now().set({ second: 0, millisecond: 0 });
}

function getOverallDefaults(currentSchedule?: Schedule) {
  return {
    frequency: currentSchedule?.frequency ?? 'None',

    periodStart: currentSchedule?.periodStart ?? now(),

    periodEnd: currentSchedule?.periodEnd ?? now().plus({ weeks: 1 }).endOf('day'),

    executionStart: (currentSchedule?.executionStart ?? now()).set(
      allDayTime('start', currentSchedule?.executionAllDay),
    ),

    executionEnd: (
      currentSchedule?.executionEnd ??
      currentSchedule?.executionStart ??
      now().plus({ hours: 1 })
    ).set(allDayTime('end', currentSchedule?.executionAllDay)),

    executionAllDay: !!currentSchedule?.executionAllDay,

    weekdays: currentSchedule?.weekdays,

    interval: currentSchedule?.interval ?? 1,

    timeZone: currentSchedule?.timeZone ?? Settings.defaultZone.name,
  } satisfies Partial<Schedule>;
}

function getDate(datetime?: DateTime) {
  return {
    year: datetime?.year,
    month: datetime?.month,
    day: datetime?.day,
  };
}

function allDayTime(time: 'end' | 'start', allDay: boolean = false) {
  if (allDay) {
    switch (time) {
      case 'start': {
        return {
          hour: 0,
          minute: 0,
        };
      }
      case 'end': {
        return {
          hour: 23,
          minute: 59,
        };
      }
    }
  } else {
    return {};
  }
}

const defaults: Record<Freq, (currentSchedule?: Schedule) => Schedule> = {
  [Freq.None]: (currentSchedule?) => ({
    ...getOverallDefaults(currentSchedule),
  }),

  [Freq.Daily]: (currentSchedule?) => ({
    ...getOverallDefaults(currentSchedule),

    weekdays: currentSchedule?.weekdays || defaultWeekdays,

    periodEnd: (currentSchedule
      ? currentSchedule.periodEnd
      : now().plus({
          weeks: 1,
        })
    )?.endOf('day'),

    executionStart: (currentSchedule ? currentSchedule?.executionStart : now())
      ?.set(allDayTime('start', currentSchedule?.executionAllDay))
      .set(getDate(currentSchedule?.periodStart)),

    executionEnd: (currentSchedule ? currentSchedule.executionEnd : now().plus({ hours: 1 }))
      ?.set(allDayTime('end', currentSchedule?.executionAllDay))
      .set(getDate(currentSchedule?.periodStart)),
  }),

  [Freq.Weekly]: (currentSchedule?) => {
    const executionAllDay = !!currentSchedule?.executionAllDay;

    return {
      ...getOverallDefaults(currentSchedule),
      periodEnd: (currentSchedule
        ? currentSchedule.periodEnd
        : now().plus({
            months: 1,
          })
      )?.endOf('day'),

      weekdays: currentSchedule?.weekdays ?? defaultWeekdays,

      executionAllDay,

      executionStart: (currentSchedule?.executionStart ?? currentSchedule?.periodStart ?? now())
        .set(allDayTime('start', executionAllDay))
        .set(getDate(currentSchedule?.periodStart)),

      executionEnd: (
        currentSchedule?.executionEnd ??
        currentSchedule?.executionStart?.plus({ hours: 1 }) ??
        now().plus({ hours: 1 })
      )
        .set(allDayTime('end', executionAllDay))
        .set(getDate(currentSchedule?.periodStart)),
    };
  },

  [Freq.Monthly]: (currentSchedule?) => ({
    ...getOverallDefaults(currentSchedule),

    periodEnd: (currentSchedule
      ? currentSchedule.periodEnd
      : now().plus({
          years: 1,
        })
    )?.endOf('day'),
  }),

  [Freq.Yearly]: (currentSchedule?) => ({
    ...getOverallDefaults(currentSchedule),

    periodEnd: (currentSchedule
      ? currentSchedule.periodEnd
      : now().plus({
          years: 2,
        })
    )?.endOf('day'),
  }),
};

function parseWithZone(isoString?: string, zone?: string | null) {
  return isoString
    ? DateTime.fromISO(isoString, {
        zone: zone || 'UTC',
      })
    : undefined;
}

function fromDto(jobSchedule: Partial<JobScheduleDto>): Schedule {
  return {
    ...jobSchedule,
    periodStart: parseWithZone(jobSchedule?.periodStart, jobSchedule?.timeZone),
    periodEnd: parseWithZone(jobSchedule?.periodEnd, jobSchedule?.timeZone),
    executionStart: parseWithZone(jobSchedule?.executionStart, jobSchedule?.timeZone),
    executionEnd: parseWithZone(jobSchedule?.executionEnd, jobSchedule?.timeZone),
  };
}

function toDto(schedule?: Schedule): Partial<JobScheduleDto> {
  return {
    ...schedule,
    periodStart: schedule?.periodStart?.toISODate() ?? undefined,
    periodEnd: schedule?.periodEnd?.toISODate() ?? undefined,
    executionStart: schedule?.executionStart?.toISO() ?? undefined,
    executionEnd: schedule?.executionEnd?.toISO() ?? undefined,
  };
}

export function getDefaults(
  frequencyType?: Freq | null,
  currentSchedule?: Partial<JobScheduleDto>,
): Partial<JobScheduleDto> {
  return toDto({
    ...(currentSchedule ? fromDto(currentSchedule) : {}),
    ...defaults[frequencyType ?? 'None'](currentSchedule ? fromDto(currentSchedule) : undefined),
    frequency: frequencyType,
  });
}
